import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Badge, Button, Card, Col, Collapse, Modal, Row, Spin, Tag, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { UploadFile } from "antd/lib/upload/interface";
import { AxiosResponse } from "axios";
import moment, { Moment } from "moment";
import React, { CSSProperties, Component, Fragment, RefObject } from "react";
import { formatSnakeCase } from "../helpers/utilities";
import {
  APIError,
  ValidateAccount,
  BankAccountTypesEnum,
  Beneficiary,
  BeneficiaryTypesEnum,
  BranchMap,
  Company,
  Customer,
  CustomerCompanyAssociation,
  FileTypes,
  FinancialInstitution,
  FormFieldConfig,
  Id,
  Opportunity,
  PaymentAccount,
  PaymentAccountTypesEnum,
  SelectOptionsInterface,
  Subscription,
  Title,
  UpdateBeneficiaryInformationFnProps,
  UpdateCustomerCompanyAssociationFnProps,
  UpdateDocumentsInformationFnProps,
  UpdatePaymentInformationFnProps,
  FileUpload,
} from "../helpers/shared-interfaces";
import {
  bankAccountTypes,
  capitalizeFirstLetter,
  disabledDate,
  generateBeneficiaryTypes,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  handleValidationErrors,
  mapAPIErrorToFormError,
  paymentAccountTypes,
  request,
  responseHandler,
  filterActiveSubscriptions,
} from "../helpers/utilities";
import BaseForm from "./Form/BaseForm";
import EntitledSection from "./EntitledSection";
import entitlements from "../config/entitlements";
import ModalCreator from "./ModalCreator";
import { PaymentAccountCard } from "./PaymentsAccountsView";
import {
  DELETE_FILE,
  GET_CUSTOMER_RELATIONSHIPS,
  GET_ID_TYPES,
  GET_TITLES,
} from "../gql/general";
import { SEARCH_COMPANY_SUMMARY } from "../gql/companies";
import {
  CREATE_DEBIT_ACCOUNT,
  CREATE_PAYOUT_ACCOUNT,
  DELETE_DEBIT_ACCOUNT,
  DELETE_PAYOUT_ACCOUNT,
  UPDATE_DEBIT_ACCOUNT,
  UPDATE_PAYOUT_ACCOUNT,
  VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH,
} from "../gql/accounts";
import {
  CREATE_CUSTOMER_COMPANY_ASSOCIATION,
  DELETE_CUSTOMER_ASSOCIATION,
  REACTIVATE_CUSTOMER_ASSOCIATION,
  UPDATE_CUSTOMER_COMPANY_ASSOCIATION,
} from "../gql/customers";
import {
  UPDATE_BENEFICIARY,
  CREATE_BENEFICIARY,
  DELETE_BENEFICIARY,
} from "../gql/beneficiaries";
import { UploadedDocumentCard } from "./CompanyOnboardingDetails";
import { DEBOUNCE_TIME, frequencyOfIncomeOptions, natureOfEmploymentOptions, PAYOUT_VALIDATION_THRESHOLD } from "../helpers/constants";
import debounce from "lodash.debounce";

const { Panel } = Collapse;

const colStyle: CSSProperties = {
  paddingRight: "20px",
};

type UnionOfEntities =
  | PaymentAccount
  | Beneficiary
  | CustomerCompanyAssociation;

export enum Views {
  payout = "Payout Account Information",
  debit = "Debit Account Information",
  beneficiary = "Beneficiary Information",
  documentUploads = "Required Documents",
  customerAssociation = "Customer Company Association",
}

export enum DocumentTypes {
  directDebitForm = "Direct Debit Form",
  signedApplicationForm = "Signed Application Form",
}

interface CustomCardProps<T> {
  type: string;
  toggleDeleteConfirmationModal?: (id?: string) => void;
  setEntityToBeDeleted?: (entity: string) => void;
  setIdToBeDeleted?: (id: string) => void;
  setCurrentlyEditing?: (type: string, obj: T) => void;
  isEditing?: boolean;
  entitlement?: string;
  deleteAssocation?: (id: string) => void;
  deletePayoutAccount?: (id: string) => void;
  deleteBeneficiaryAccount?: (id: string) => void;
  isActive?: boolean;
  markAsFormer?: (id: string) => void;
  markAsCurrent?: (id: string) => void;
}


interface PaymentAccountCardWrapperProps
  extends CustomCardProps<PaymentAccount> {
  paymentAccount: PaymentAccount;
}

interface BeneficiaryCardProps extends CustomCardProps<Beneficiary> {
  beneficiary: Beneficiary;
}

interface CustomerAssociationCardProps
  extends CustomCardProps<CustomerCompanyAssociation> {
  association: CustomerCompanyAssociation;
}

interface CardCreatorProps {
  id: string;
  leftHandSideUI: JSX.Element;
  deleteBtn: JSX.Element;
  editButton?: JSX.Element;
  isEditing?: boolean;
  inactiveTag?: JSX.Element;
  isActive?: boolean;
  markAsFormerBtn?: JSX.Element;
  markAsCurrentBtn?: JSX.Element;
}

export function createBeneficiaryFormFields(props: {
  createBeneficiary: () => void;
  updateBeneficiary: () => void;
  fileList: UploadFile[];
  relationships: SelectOptionsInterface[];
  idTypes: Id[];
  titles: Title[];
  subscriptions: SelectOptionsInterface[];
  initialValue?: Beneficiary;
  isEditing: boolean;
  handleBeneficiaryTypeChange: (value: string) => void;
  handleDateOfBirthChange: (value: Moment | null) => void;
  beneficiaryAge: number;
  beneficiaryType: string;
  cancelEdit?: () => void;
}): FormFieldConfig[] {
  const isInstitution = props.beneficiaryType === "institution";
  const fields: FormFieldConfig[] = [
    {
      id: "beneficiaryType",
      name: "beneficiaryType",
      inputType: "select",
      rule: "string",
      required: true,
      disabled: false,
      changeHandler: props.handleBeneficiaryTypeChange,
      initialValue:
        props.initialValue?.beneficiaryType || props.beneficiaryType,
      selectOptions: generateBeneficiaryTypes(),
      placeholder: "Beneficiary Type",
      label: "Beneficiary Type",
      colConfig: {
        span: 12,
      },
    },
    {
      id: "titleId",
      name: "titleId",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Title",
      label: "Title",
      selectOptions: props.titles,
      initialValue: props.initialValue?.title
        ? capitalizeFirstLetter(props.initialValue?.title.name)
        : undefined,
      colConfig: {
        offset: 1,
        span: 11,
      },
    },
    {
      id: "firstName",
      name: "firstName",
      inputType: "string",
      rule: "string",
      placeholder: "First Name",
      label: "First Name",
      initialValue: props.initialValue?.firstName,
      required: true,
      colConfig: {
        span: 12,
      },
    },
    {
      id: "middleName",
      name: "middleName",
      inputType: "string",
      rule: "string",
      placeholder: "Middle Name",
      initialValue: props.initialValue?.middleName,
      label: "Middle Name",
      required: false,
      colConfig: {
        offset: 1,
        span: 11,
      },
    },
    {
      id: "lastName",
      name: "lastName",
      initialValue: props.initialValue?.lastName,
      inputType: "string",
      rule: "string",
      placeholder: "Last Name",
      label: "Last Name",
      required: true,
      colConfig: {
        span: 12,
      },
    },

    {
      id: "emailAddress",
      name: "emailAddress",
      inputType: "string",
      initialValue: props.initialValue?.emailAddress,
      rule: "email",
      label: "Email",
      placeholder: "Email Address",
      required: false,
      colConfig: {
        offset: 1,
        span: 11,
      },
    },

    {
      id: "idTypeId",
      name: "idTypeId",
      inputType: "select",
      rule: "string",
      required: false,
      disabled: false,
      selectOptions: props.idTypes,
      initialValue: props.initialValue?.idType
        ? capitalizeFirstLetter(props.initialValue?.idType.name)
        : undefined,
      placeholder: "ID Type",
      label: "ID Type",
      colConfig: {
        span: 12,
      },
    },

    {
      id: "idNumber",
      name: "idNumber",
      inputType: "string",
      rule: "string",
      required: false,
      disabled: false,
      placeholder: "Enter ID Number",
      initialValue: props.initialValue?.idNumber,

      label: "ID Number",
      colConfig: {
        offset: 1,
        span: 11,
      },
    },

    {
      id: "dateOfBirth",
      name: "dateOfBirth",
      inputType: "defaultDate",
      rule: "object",
      placeholder: "Date of birth",
      dateChangeHandler: props.handleDateOfBirthChange,
      initialValue:
        props.initialValue?.dateOfBirth &&
        moment(props.initialValue.dateOfBirth),

      label: "Date of birth",
      required: true,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 12,
      },
    },

    {
      id: "customerRelationshipId",
      name: "customerRelationshipId",
      inputType: "select",
      rule: "string",
      placeholder: "Relationship",
      initialValue:
        props.initialValue?.customerRelationship &&
        props.initialValue?.customerRelationship.id,
      label: "Relationship To Customer",
      selectOptions: props.relationships,
      required: true,
      colConfig: {
        offset: 1,
        span: 11,
      },
    },

    {
      id: "customerSubscriptionId",
      name: "customerSubscriptionId",
      inputType: "select",
      rule: "string",
      placeholder: "Subscription",
      initialValue:
        props.initialValue?.customerSubscription?.id ??
        (props.subscriptions.length === 1
          ? props.subscriptions[0].id
          : undefined),
      label: "Select Subscription",
      selectOptions: props.subscriptions,
      required: true,
      colConfig: {
        span: 12,
      },
    },

    {
      id: "phoneNumber",
      name: "phoneNumber",
      inputType: "phone",
      rule: "string",
      label: "Phone",
      placeholder: "Phone",
      required: false,
      initialValue: props.initialValue?.phoneNumber,
      elementStyle: { width: "100%" },
      colConfig: {
        offset: 1,
        span: 11,
      },
    },

    {
      id: "benefitsPercentage",
      name: "benefitsPercentage",
      inputType: "number",
      initialValue: props.initialValue?.benefitsPercentage,
      label: "Benefits Percentage",
      placeholder: "eg. 62.5%",
      required: true,
      elementStyle: { width: "100%" },
      rule: "number",
      colConfig: {
        span: isInstitution ? 11 : 12,
        offset: isInstitution ? 1 : 0,
      },
    },

    {
      id: "guardianName",
      name: "guardianName",
      inputType: "string",
      initialValue: props.initialValue?.guardianName,
      label: "Guardian Name",
      placeholder: "Guardian Name",
      required: false,
      elementStyle: { width: "100%" },
      rule: "string",
      colConfig: {
        offset: 1,
        span: 11,
      },
    },
    {
      id: "guardianEmail",
      name: "guardianEmail",
      inputType: "string",
      initialValue: props.initialValue?.guardianEmail,
      label: "Guardian Email",
      placeholder: "Guardian Email",
      required: false,
      elementStyle: { width: "100%" },
      rule: "string",
      colConfig: {
        span: 12,
      },
    },
    {
      id: "guardianPhoneNumber",
      name: "guardianPhoneNumber",
      inputType: "phone",
      initialValue: props.initialValue?.guardianPhoneNumber,
      label: "Guardian Phone Number",
      placeholder: "Guardian Phone Number",
      required: false,
      elementStyle: { width: "100%" },
      rule: "string",
      colConfig: {
        offset: 1,
        span: 11,
      },
    },
    {
      id: "guardianAddress",
      name: "guardianAddress",
      inputType: "string",
      initialValue: props.initialValue?.guardianAddress,
      label: "Guardian Address",
      placeholder: "Guardian Address",
      required: false,
      elementStyle: { width: "100%" },
      rule: "string",
      colConfig: {
        span: 12,
      },
    },
    {
      id: "institutionName",
      name: "institutionName",
      inputType: "string",
      initialValue: props.initialValue?.institutionName,
      label: "Institution Name",
      placeholder: "Institution Name",
      required: true,
      elementStyle: { width: "100%" },
      rule: "string",
      colConfig: {
        span: 12
      },
    },
    {
      id: "institutionPhoneNumber",
      name: "institutionPhoneNumber",
      inputType: "phone",
      initialValue: props.initialValue?.institutionPhoneNumber,
      label: "Institution Phone Number",
      placeholder: "Institution Phone Number",
      required: true,
      elementStyle: { width: "100%" },
      rule: "string",
      colConfig: {
        span: 11,
        offset: 1,
      },
    },
    {
      id: "institutionAddress",
      name: "institutionAddress",
      inputType: "string",
      initialValue: props.initialValue?.institutionAddress,
      label: "Institution Address",
      placeholder: "Institution Address",
      required: true,
      elementStyle: { width: "100%" },
      rule: "string",
      colConfig: {
        span: 12,
      },
    },

    ...(props.initialValue && props.cancelEdit
      ? [
          {
            id: "beneficiaryButtonGroup",
            name: "beneficiaryButtonGroup",
            inputType: "buttonGroup" as const,
            required: false,
            elementStyle: {
              position: "relative",
              textAlign: "left",
            },
            buttonGroup: [
              {
                label: "Update",
                className: "base-button",
                onClick: props.updateBeneficiary,
              },
              {
                label: "Cancel Edit",
                className: "warning-btn",
                onClick: props.cancelEdit,
              },
            ],
            colConfig: {
              span: 24,
            },
          } as FormFieldConfig,
        ]
      : [
          {
            id: "createBeneficiary",
            name: "createBeneficiary",
            inputType: "button" as const,
            label: "Create Beneficiary",
            className: "base-button",
            handleClick: props.createBeneficiary,
            required: false,
            value: "Save",
            elementStyle: {
              position: "relative",
              top: "-20px",
              textAlign: "left",
            },
            colConfig: {
              span: 24,
            },
          } as FormFieldConfig,
        ]),
  ];

  return fields;
}

export function createPaymentAccountFormFields(props: {
  createPaymentAccount: () => void;
  updatePaymentAccount: () => void;
  isEditing: boolean;
  initialValue?: PaymentAccount;
  name: string;
  financialInstitutions: SelectOptionsInterface[];
  branches: SelectOptionsInterface[];
  handleAccountTypeChange?: (value: string) => void;
  handleAccountTypeSelect?: (value: string) => void;
  handleFinancialInstitutionChange: (value: string) => void;
  handleFileUploadChange?: (field: string, combinedInfo: string) => void;
  paymentAccountType: string;
  subscriptions?: SelectOptionsInterface[];
  accountValidationResponse?: ValidateAccount;
  accountNumber?: string;
  payoutFinancialInstitutionId?: string;
  institutions?: SelectOptionsInterface[];
  verifyAccount?: () => Promise<void>;
  handleAccountNumberChange?: (number: React.ChangeEvent<HTMLInputElement>) => void;
  isDebit?: boolean;
}): FormFieldConfig[] {
  let fields: FormFieldConfig[] = [
    {
      id: "accountType",
      name: "accountType",
      placeholder: "Type",
      inputType: "select",
      rule: "string",
      label: "Payment Account Type",
      selectHandler: props.handleAccountTypeSelect,
      changeHandler: props.handleAccountTypeChange,
      selectOptions: [
        {
          id: PaymentAccountTypesEnum.bankAccount,
          name: paymentAccountTypes.bank_account,
          value: PaymentAccountTypesEnum.bankAccount,
        },
      ],
      initialValue: props.initialValue?.accountType || props.paymentAccountType,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "financialInstitutionId",
      name: "financialInstitutionId",
      placeholder: "Institution Name",
      inputType: "select",
      selectHandler: props.handleFinancialInstitutionChange,
      selectOptions: props.institutions,
      rule: "string",
      label: "Institution Name",
      initialValue: props.payoutFinancialInstitutionId,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "accountNumber",
      name: "accountNumber",
      placeholder: "Account Number",
      inputType: "string",
      rule: "string",
      defaultValue: props.accountNumber,
      label: "Account Number",
      onChange: props.handleAccountNumberChange,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "accountName",
      name: "accountName",
      inputType: "description",
      rule: "string",
      required: false,
      label: "Account Name",
      value: props.accountValidationResponse?.name,
      colConfig: {
        span: 24,
      },
    },
    ...(props.verifyAccount
      ? [
          {
            id: "verifyPayoutAccount",
            name: "verifyPayoutAccount",
            inputType: "button",
            required: false,
            className: "base-button",
            handleClick: props.verifyAccount,
            value: "Verify",
            elementStyle: {
              position: "relative",
              textAlign: "left",
            },
            colConfig: {
              span: 24,
            },
          } as FormFieldConfig,
        ]
      : []),
    {
      id: "bankBranchId",
      name: "bankBranchId",
      placeholder: "Branch",
      inputType: "select",
      rule: "string",
      label: "Branch",
      selectOptions: props.branches,
      initialValue: props.initialValue?.bankBranch?.id,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "customerSubscriptionId",
      name: "customerSubscriptionId",
      placeholder: "Product Subscription",
      inputType: "select",
      rule: "string",
      label: "Customer Subscription",
      selectOptions: props.subscriptions,
      initialValue: props.initialValue?.customerSubscription?.id,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "bankAccountType",
      name: "bankAccountType",
      placeholder: "Type",
      inputType: "select",
      rule: "string",
      label: "Bank Account Type",
      selectOptions: [
        {
          id: BankAccountTypesEnum.currentAccount,
          name: bankAccountTypes.current_account,
          value: BankAccountTypesEnum.currentAccount,
        },
        {
          id: BankAccountTypesEnum.savingsAccount,
          name: bankAccountTypes.savings_account,
          value: BankAccountTypesEnum.savingsAccount,
        },
      ],
      initialValue: props.initialValue?.bankAccountType,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "amount",
      name: "amount",
      placeholder: "Amount",
      inputType: "number",
      rule: "number",
      initialValue: props.initialValue?.amount,
      label: "Amount",
      required: true,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "authorizationFormUrl",
      name: "authorizationFormUrl",
      inputType: "fileUpload",
      initialValue: props.initialValue?.authorizationFormUrl,
      rule: "string",
      required: false,
      changeHandler: (combinedInfo: string) => props.handleFileUploadChange?.("authorizationFormUrl", combinedInfo),
      disabled: false,
      label: "Debit Authorization Form",
      colConfig: {
        span: 24,
      },
    },
    {
      id: "expirationDate",
      name: "expirationDate",
      inputType: "date",
      rule: "string",
      placeholder: "Valid Until",
      label: "Valid Until",
      initialValue: props.initialValue?.expirationDate,
      required: true,
      elementStyle: {
        width: "100%",
      },
      disabledDate: disabledDate,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "createPayoutAccount",
      name: "createPayoutAccount",
      inputType: "button",
      label: props.isDebit ? "Create Debit Account" : "Create Payout Account",
      disabled: props.isDebit ? false : !props.accountValidationResponse?.name,
      className: "base-button",
      handleClick:
        props.isEditing && props.initialValue
          ? props.updatePaymentAccount
          : props.createPaymentAccount,
      required: false,
      value: props.initialValue
        ? "Update"
        : props.isDebit
        ? "Save Debit Account"
        : "Save",
      elementStyle: {
        position: "relative",
        top: "-40px",
        textAlign: "left",
      },
      colConfig: {
        span: 24,
      },
    },
  ];

  if (!props.subscriptions) {
    fields = fields.filter((item) => item.id !== "customerSubscriptionId");
  }

  if (props.isDebit && props.isEditing && props.initialValue) {
    fields = fields.filter((item) => item.id !== "customerSubscriptionId");
  }

  return fields;
}

export function createDocumentUploadFormFields(props: {
  opportunity: Opportunity;
  customer?: Customer;
  initialValues: UpdateDocumentsInformationFnProps;
  handleFileUploadSuccess: (value: string) => void;
}): FormFieldConfig[] {
  return [
    {
      id: "signedApplicationFormUrl",
      name: "signedApplicationFormUrl",
      initialValue: props.initialValues.signedApplicationForm?.url,
      inputType: "fileUpload",
      rule: "string",
      required: false,
      disabled: false,
      label: "Signed Application Form",
      changeHandler: props.handleFileUploadSuccess,
      fileUploadProps: {
        type: FileTypes.signedApplicationForm,
        opportunityId: props.opportunity.id,
        customerId: props.customer?.id,
        skipJobProcess: true,
      },
      colConfig: {
        span: 17,
      },
    },
    {
      id: "directDebitAuthorizationForm",
      name: "directDebitAuthorizationForm",
      initialValue: props.initialValues.directDebitAuthorizationForm?.url,
      inputType: "fileUpload",
      rule: "string",
      required: false,
      disabled: false,
      label: "Direct Debit Authorization Form",
      changeHandler: props.handleFileUploadSuccess,
      fileUploadProps: {
        type: FileTypes.directDebitAuthorizationForm,
        opportunityId: props.opportunity.id,
        customerId: props.customer?.id,
        skipJobProcess: true,
      },
      colConfig: {
        span: 17,
      },
    },
  ];
}

export function createCustomerCompanyAssociationFormFields(props: {
  createAssociation: () => void;
  updateAssociation: () => void;
  companies: Company[];
  initialValue?: CustomerCompanyAssociation;
  isSearchingCompanies?: boolean;
  cancelEdit?: () => void;
  handleCompanySearch?: (value: string) => void;
  isEditing: boolean;
}): FormFieldConfig[] {
  return [
    {
      id: "companyId",
      name: "companyId",
      rule: "string",
      inputType: "search",
      placeholder: "Company",
      label: "Company",
      initialValue: props.initialValue?.company?.id ?? undefined,
      selectOptions: props.companies,
      isSearching: props.isSearchingCompanies,
      changeHandler: props.handleCompanySearch,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "relationship",
      name: "relationship",
      inputType: "string",
      rule: "string",
      placeholder: "Relationship To Company",
      label: "Job Title",
      initialValue: props.initialValue?.relationship,
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "natureOfEmployment",
      name: "natureOfEmployment",
      inputType: "select",
      rule: "string",
      placeholder: "Nature Of Employment",
      label: "Nature Of Employment",
      initialValue: props.initialValue?.natureOfEmployment,
      selectOptions: natureOfEmploymentOptions,
      required: false,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "frequencyOfIncome",
      name: "frequencyOfIncome",
      inputType: "select",
      rule: "string",
      placeholder: "Frequency Of Income",
      label: "Frequency Of Income",
      initialValue: props.initialValue?.frequencyOfIncome,
      selectOptions: frequencyOfIncomeOptions,
      required: false,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "staffId",
      name: "staffId",
      inputType: "string",
      rule: "string",
      placeholder: "Staff ID",
      label: "Staff ID",
      initialValue: props.initialValue?.staffId,
      required: false,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "influenceLevel",
      name: "influenceLevel",
      inputType: "select",
      rule: "string",
      placeholder: "Level Of Influence",
      label: "Level Of Influence",
      initialValue: props.initialValue?.influenceLevel,
      selectOptions: [
        { id: "0", name: "Low", value: "low" },
        { id: "1", name: "Medium", value: "medium" },
        { id: "2", name: "High", value: "high" },
      ],
      required: false,
      colConfig: {
        span: 12,
      },
    },
    {
      id: "dateOfEmployment",
      name: "dateOfEmployment",
      inputType: "defaultDate",
      rule: "object",
      placeholder: "Date Of Employment",
      label: "Date Of Employment",
      initialValue:
        props.initialValue?.dateOfEmployment &&
        moment(props.initialValue?.dateOfEmployment),
      required: false,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "createAssociation",
      name: "createAssociation",
      inputType: "button",
      label: "Create",
      className: "base-button",
      handleClick:
        props.isEditing && props.initialValue
          ? props.updateAssociation
          : props.createAssociation,
      required: false,
      value: props.initialValue ? "Update" : "Save",
      elementStyle: {
        position: "relative",
        top: "-40px",
        textAlign: "left",
      },
      colConfig: {
        span: 24,
      },
    },
  ];
}

export function CardCreator(props: CardCreatorProps): JSX.Element {
  return (
    <Card
      className="associated-entity-card"
      style={{ marginBottom: "10px" }}
      key={props.id}
    >
      <Row>
        <Col
          span={11}
          style={{
            marginTop: "11px",
          }}
        >
          {props.leftHandSideUI}
        </Col>
        <Col
          span={13}
          style={{
            textAlign: "right",
          }}
        >
          {props.isActive && props.isEditing && props.editButton}
          {props.isActive && props.isEditing && props.markAsFormerBtn}
          {props.isActive && props.isEditing && props.markAsCurrentBtn}
          {props.isActive && props.deleteBtn}
          {!props.isActive && props.inactiveTag}
        </Col>
      </Row>
    </Card>
  );
}

export const BeneficiaryAccountCard = function (
  props: BeneficiaryCardProps,
): JSX.Element {
  const entitlement = props.entitlement || entitlements.Customers.edit;
  return CardCreator({
    id: props.beneficiary.id,
    isEditing: props.isEditing,
    leftHandSideUI: (
      <div>
        <p className="associated-entity-paragraph">
          {props.beneficiary.fullname}
        </p>

        <p className="associated-entity-paragraph">
          {props.beneficiary.institutionName &&
            props.beneficiary.institutionName}
        </p>
        <p className="associated-entity-paragraph">
          {props.beneficiary.institutionAddress &&
            props.beneficiary.institutionAddress}
        </p>
        <p className="associated-entity-paragraph">
          {props.beneficiary.institutionPhoneNumber &&
            props.beneficiary.institutionPhoneNumber}
        </p>
        <p className="associated-entity-paragraph">
          {`${String(props.beneficiary.benefitsPercentage)}%`}
        </p>
        <p className="associated-entity-paragraph">
          {props.beneficiary.dateOfBirth}
        </p>
        <p className="associated-entity-paragraph">
          {props.beneficiary.customerRelationship &&
            props.beneficiary.customerRelationship.name}
        </p>

        <p className="associated-entity-paragraph">
          {props.beneficiary.phoneNumber}
        </p>
        <p className="associated-entity-paragraph">
          {props.beneficiary.emailAddress}
        </p>
        {props.beneficiary.idType && (
          <p className="associated-entity-paragraph">
            {props.beneficiary.idType.name}
          </p>
        )}
        <p className="associated-entity-paragraph">
          {props.beneficiary.idNumber}
        </p>
        <p className="associated-entity-paragraph">
          {props.beneficiary.customerSubscription?.product?.name}
        </p>
        {props.beneficiary.deletedAt && <p className="associated-entity-paragraph">
          Deleted On:{moment(props.beneficiary.deletedAt, "YYYY-MM-DD HH:mm:ss").format(
            "llll",
          )}
        </p>}
      </div>
    ),
    inactiveTag: (
      <Tag
        color="#002c6d"
        style={{
          marginBottom: "2px",
        }}
      >
        Previous
      </Tag>
    ),
    isActive: props.beneficiary.isActive,
    deleteBtn: (
      <EntitledSection entitlement={entitlement}>
        <Button
          id="beneficiary-card-delete-btn"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            props.deleteBeneficiaryAccount?.(props.beneficiary.id);
          }}
        >
          <DeleteOutlined /> Delete
        </Button>
      </EntitledSection>
    ),
    editButton: (
      <EntitledSection entitlement={entitlement}>
        <Button
          id="beneficiary-card-edit-btn"
          type="dashed"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            props.setCurrentlyEditing &&
              props.setCurrentlyEditing(props.type, props.beneficiary);
          }}
        >
          <EditOutlined /> Edit
        </Button>
      </EntitledSection>
    ),
  });
};

export const PaymentAccountCardWrapper = function (
  props: PaymentAccountCardWrapperProps,
): JSX.Element {
  const entitlement = props.entitlement || entitlements.Customers.edit;
  const ui: JSX.Element = PaymentAccountCard({
    paymentAccount: props.paymentAccount,
  });

  return CardCreator({
    id: props.paymentAccount.id,
    isEditing: props.isEditing,
    leftHandSideUI: ui,
    deleteBtn: (
      <EntitledSection entitlement={entitlement}>
        <Button
          id="payment-card-delete-btn"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            props.deletePayoutAccount?.(props.paymentAccount.id);
          }}
        >
          <DeleteOutlined /> Delete
        </Button>
      </EntitledSection>
    ),
    editButton: (
      <EntitledSection entitlement={entitlement}>
        <Button
          id="payment-card-edit-btn"
          type="dashed"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            props.setCurrentlyEditing &&
              props.setCurrentlyEditing(props.type, props.paymentAccount);
          }}
        >
          <EditOutlined /> Edit
        </Button>
      </EntitledSection>
    ),
  });
};

export const CustomerAssociationCardWrapper = function (
  props: CustomerAssociationCardProps,
): JSX.Element {
  const entitlement = props.entitlement || entitlements.Customers.edit;
  return CardCreator({
    isActive: props.association.isActive,
    id: props.association.id,
    isEditing: props.isEditing,
    leftHandSideUI: (
      <div>
        <p className="associated-entity-paragraph">
          {props.association.company.name}
        </p>
        <p className="associated-entity-paragraph">
          {props.association.company.petraId}
        </p>
        <p className="associated-entity-paragraph">
          {props.association.staffId}
        </p>
        <p className="associated-entity-paragraph">
          {props.association.relationship}
        </p>
        <p className="associated-entity-paragraph">
          {formatSnakeCase(capitalizeFirstLetter(props.association.frequencyOfIncome || ""))}
        </p>
        <p className="associated-entity-paragraph">
          {formatSnakeCase(capitalizeFirstLetter(props.association.natureOfEmployment || ""))}
        </p>
        {props.association.dateOfEmployment && (
          <p className="associated-entity-paragraph">
            {moment(props.association.dateOfEmployment).format("LL")}
          </p>
        )}
        {!props.association.isEmployee && (
          <Tag
            color="red"
            style={{
              marginBottom: "2px",
            }}
          >
            Former Employer
          </Tag>
        )}
      </div>
    ),
    deleteBtn: (
      <EntitledSection
        entitlement={entitlements.Customers.companyAssociation.delete}
      >
        <Button
            id="association-card-delete-btn"
            style={{ marginRight: "9px", marginTop: "6px" }}
            onClick={(): void => {
              props.deleteAssocation?.(props.association.id);
            }}
          >
            <DeleteOutlined /> Delete
          </Button>
      </EntitledSection>
    ),
    editButton: (
      <EntitledSection entitlement={entitlement}>
        <Button
          id="association-card-edit-btn"
          type="dashed"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            props.setCurrentlyEditing &&
              props.setCurrentlyEditing(props.type, props.association);
          }}
        >
          <EditOutlined /> Edit
        </Button>
      </EntitledSection>
    ),
    markAsFormerBtn: props.association.isEmployee ? (
      <Button
        id="association-card-mark-former-btn"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => { props.markAsFormer?.(props.association.id); }}
      >
        Mark as Former
      </Button>
    ) : undefined,
    markAsCurrentBtn: !props.association.isEmployee ? (
      <Button
        id="association-card-mark-current-btn"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => { props.markAsCurrent?.(props.association.id); }}
      >
        Mark as Current
      </Button>
    ) : undefined,
  });
};

export const createUploadedDocumentObject = function (props: UpdateDocumentsInformationFnProps): FileUpload[] {
  const response: FileUpload[] = [];
  if (props.signedApplicationForm) {
    response.push({
      id: DocumentTypes.signedApplicationForm,
      fileType: DocumentTypes.signedApplicationForm,
      url: props.signedApplicationForm.url,
    });
  }

  if (props.directDebitAuthorizationForm) {
    response.push({
      id: DocumentTypes.directDebitForm,
      fileType: DocumentTypes.directDebitForm,
      url: props.directDebitAuthorizationForm.url,
    });
  }

  if (props.customerInfoFileUploads) {
    props.customerInfoFileUploads.forEach(fileUpload => {
      response.push({
        id: fileUpload.id,
        fileType: fileUpload.fileType,
        url: fileUpload.url,
      });
    });
  }

  return response;
};

export function ModalToggler({
  modalTarget,
  showModal,
  deletePayoutAccount,
  deleteBeneficiaryAccount,
  deleteDebitAccount,
  toggleModal,
  deleteSignedApplicationForm,
  deleteAssociation,
  deleteCustomerDocument
}: {
  modalTarget: string | null;
  showModal: boolean;
  deleteDebitAccount: () => void;
  deleteBeneficiaryAccount: (id: string) => void;
  deleteSignedApplicationForm: () => void;
  deleteAssociation: (id: string) => Promise<void>;
  deletePayoutAccount: (id: string) => Promise<void>;
  toggleModal: () => void;
  deleteCustomerDocument: () => void;
}): JSX.Element {
  let modal = <div />;

  switch (modalTarget) {
    case Views.payout:
      modal = ModalCreator({
        showModal,
        onOkay: () => deletePayoutAccount,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this record?",
      });
      break;
    case Views.debit:
      modal = ModalCreator({
        showModal,
        onOkay: deleteDebitAccount,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this record?",
      });
      break;
    case Views.beneficiary:
      modal = ModalCreator({
        showModal,
        onOkay: () => deleteBeneficiaryAccount,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this record?",
      });
      break;
    case DocumentTypes.signedApplicationForm:
      modal = ModalCreator({
        showModal,
        onOkay: deleteSignedApplicationForm,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this document?",
      });
      break;
    case Views.customerAssociation:
      modal = ModalCreator({
        showModal,
        onOkay: () => deleteAssociation,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this association?",
      });
      break;
    default:
      modal = ModalCreator({
        showModal,
        onOkay: deleteCustomerDocument,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this document?",
      });
  }
  return modal;
}

export function AssociatedEntityListCreator({
  toggleDeleteConfirmationModal,
  setIdToBeDeleted,
  setEntityToBeDeleted,
  setCurrentlyEditing,
  isEditing,
  entitlement,
  deleteAssocation,
  deletePayoutAccount,
  deleteBeneficiaryAccount,
  markAsFormer,
  markAsCurrent
}: {
  toggleDeleteConfirmationModal: (id?: string) => void;
  setIdToBeDeleted: (id: string) => void;
  setEntityToBeDeleted: (id: string) => void;
  deleteAssocation: (id: string) => Promise<void>;
  deletePayoutAccount: (id: string) => Promise<void>;
  deleteBeneficiaryAccount: (id: string) => Promise<void>;
  markAsFormer: (id: string) => Promise<void>;
  markAsCurrent: (id: string) => Promise<void>;
  setCurrentlyEditing?: (
    type: string,
    obj: PaymentAccount | Beneficiary | CustomerCompanyAssociation,
  ) => void;
  isEditing?: boolean;
  entitlement?: string;
}): ({
  entity,
  array,
}: {
  entity: string;
  array:
  | PaymentAccount[]
  | Beneficiary[]
  | FileUpload[]
  | CustomerCompanyAssociation[];
}) => JSX.Element[] {
  return ({
    entity,
    array,
  }: {
    entity: string;
    array:
    | PaymentAccount[]
    | Beneficiary[]
    | FileUpload[]
    | CustomerCompanyAssociation[];
  }): JSX.Element[] => {
    const elements = [];
    switch (entity) {
      case Views.payout:
        for (const item of array) {
          elements.push(
            PaymentAccountCardWrapper({
              isEditing,
              paymentAccount: item as PaymentAccount,
              setEntityToBeDeleted,
              toggleDeleteConfirmationModal,
              setCurrentlyEditing,
              setIdToBeDeleted,
              deletePayoutAccount,
              type: Views.payout,
              entitlement,
            }),
          );
        }
        break;
      case Views.debit:
        for (const item of array) {
          elements.push(
            PaymentAccountCardWrapper({
              isEditing,
              paymentAccount: item as PaymentAccount,
              setEntityToBeDeleted,
              toggleDeleteConfirmationModal,
              setCurrentlyEditing,
              setIdToBeDeleted,
              deletePayoutAccount,
              type: Views.debit,
              entitlement,
            }),
          );
        }
        break;
      case Views.beneficiary:
        for (const item of array) {
          elements.push(
            BeneficiaryAccountCard({
              isEditing,
              beneficiary: item as Beneficiary,
              setEntityToBeDeleted,
              toggleDeleteConfirmationModal,
              setIdToBeDeleted,
              setCurrentlyEditing,
              deleteBeneficiaryAccount,
              type: Views.beneficiary,
              entitlement,
            }),
          );
        }
        break;

      case Views.documentUploads: {
        const docCols: JSX.Element[] = [];
        for (const item of array) {
          const doc = item as FileUpload;
          docCols.push(
            <Col key={doc.id} xs={24} sm={12} md={8} lg={8} xl={8}>
              {UploadedDocumentCard({
                document: doc,
                setEntityToBeDeleted,
                toggleDeleteConfirmationModal: () =>
                  toggleDeleteConfirmationModal(item?.id),
                type: doc.fileType,
                setIdToBeDeleted,
                isEditing: true,
              })}
            </Col>,
          );
        }
        elements.push(
          <Row gutter={[16, 16]} key="document-uploads-row">
            {docCols}
          </Row>,
        );
        break;
      }

      case Views.customerAssociation:
        for (const item of array) {
          // Hide archived associations entirely — Delete is one-way from the UI.
          if (!(item as CustomerCompanyAssociation).isActive) continue;
          elements.push(
            CustomerAssociationCardWrapper({
              association: item as CustomerCompanyAssociation,
              setEntityToBeDeleted,
              toggleDeleteConfirmationModal,
              type: Views.customerAssociation,
              setCurrentlyEditing,
              setIdToBeDeleted,
              isEditing,
              deleteAssocation,
              entitlement,
              markAsFormer,
              markAsCurrent
            }),
          );
        }
        break;
    }
    return elements;
  };
}

interface Props {
  financialInstitutions: FinancialInstitution[];
  subscriptions: Subscription[];
  customerAssociations: CustomerCompanyAssociation[];
  activekey?: string;
  visibleViews?: Views[];
  showList?: boolean;
  renderInline?: boolean;
  showInlineHeader?: boolean;
  onCancelEdit?: () => void;
  hideFormOnDelete?: boolean;
  isEditing: boolean;
  opportunity: Opportunity;
  companies?: Company[];
  updateCustomerPaymentInformation?: (
    props: UpdatePaymentInformationFnProps,
  ) => void;
  updateBeneficiaryInformation?: (
    beneficiaryInfo: UpdateBeneficiaryInformationFnProps,
  ) => void;
  updateCustomerCompanyAssociationsInformation?: (
    props: UpdateCustomerCompanyAssociationFnProps,
  ) => void;
  showBeneficiarySection?: boolean;
  showCustomerAssociationSection?: boolean;
  showOnboardingDetailsSection?: boolean;

  branchMap: BranchMap;
  loadOpportunity: () => void;
  customer?: Customer;
}

interface State {
  id: string;
  modalTarget: string;
  idToBeDeleted?: string;
  showDeleteConfirmationModal: boolean;
  isLoading: boolean;
  relationships: SelectOptionsInterface[];
  customerBeneficiaries: Beneficiary[];
  payoutAccountBeingEdited?: PaymentAccount;
  debitAccountBeingEdited?: PaymentAccount;
  beneficiaryBeingEdited?: Beneficiary;
  customerAssociationBeingEdited?: CustomerCompanyAssociation;
  paymentAccountType: string;
  payoutFinancialInstitutionId: string;
  debitFinancialInstitutionId: string;
  beneficiaryType: string;
  beneficiaryAge: number;
  idTypes: Id[];
  titles: Title[];
  accountNumber: string;
  accountValidationResponse?: ValidateAccount;
  institutions: FinancialInstitution[];
  authorizationFormUrl?: string;
  isSearchingCompanies: boolean;
  searchedCompanies: Company[];
}

export default class CustomerOnboardingDetails extends Component<Props, State> {
  debitReference: RefObject<FormInstance>;
  payoutReference: RefObject<FormInstance>;
  beneficiaryReference: RefObject<FormInstance>;
  docsReference: RefObject<FormInstance>;
  associationReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      id: "",
      modalTarget: "",
      isLoading: false,
      showDeleteConfirmationModal: false,
      relationships: [],
      paymentAccountType: "",
      payoutFinancialInstitutionId: "",
      debitFinancialInstitutionId: "",
      beneficiaryType: BeneficiaryTypesEnum.individual,
      beneficiaryAge: 18,
      customerBeneficiaries: [],
      idTypes: [],
      titles: [],
      accountNumber: "",
      institutions: [],
      isSearchingCompanies: false,
      searchedCompanies: [],
    };
    this.debitReference = React.createRef<FormInstance>();
    this.payoutReference = React.createRef<FormInstance>();
    this.beneficiaryReference = React.createRef<FormInstance>();
    this.docsReference = React.createRef<FormInstance>();
    this.associationReference = React.createRef<FormInstance>();
  }

  setRef = (
    name:
      | "debitReference"
      | "payoutReference"
      | "beneficiaryReference"
      | "docsReference"
      | "associationReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  handleFileUploadSuccess = (): void => {
    this.props.loadOpportunity();
  };

  handlePayoutFinancialInstitutionChange = (
    payoutFinancialInstitutionId: string,
  ): void => {
    this.setState({ payoutFinancialInstitutionId });
  };

  handleAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ accountNumber: event.target.value });
  };


  handleDebitFinancialInstitutionChange = (
    debitFinancialInstitutionId: string,
  ): void => {
    this.setState({ debitFinancialInstitutionId });
  };

  getInstitutionsForPaymentType = (
    paymentAccountType: string,
  ): FinancialInstitution[] => {
    return this.props.financialInstitutions.filter(
      (institution: FinancialInstitution): boolean => {
        return paymentAccountType === "mm_account"
          ? institution.institutionType === "mobile_money"
          : institution.institutionType === "bank";
      },
    );
  };

  handlePaymentTypeSelect = (paymentAccountType: string): void => {
    const institutions = this.getInstitutionsForPaymentType(paymentAccountType);
    this.setState({ paymentAccountType, institutions, accountValidationResponse: undefined });
  };

  handlePaymentTypeChange = (): void => {
    this.setState({
      accountNumber: "",
      accountValidationResponse: undefined,
      payoutFinancialInstitutionId: "",
      debitFinancialInstitutionId: "",
    });
  };

  handleDateOfBirthChange = (beneficiaryDateOfBirth: Moment | null): void => {
    const age = beneficiaryDateOfBirth?.diff(moment(), "years");
    let realAge = 0;
    if (age === undefined) {
      realAge = 1;
    } else {
      realAge = age;
    }

    this.setState({
      beneficiaryAge: Math.abs(realAge),
    });
  };

  handleBeneficiaryTypeChange = (beneficiaryType: string): void => {
    this.setState({ beneficiaryType });
  };

  setEntityToBeDeleted = (modalTarget: string): void => {
    this.setState({ modalTarget });
  };

  toggleDeleteConfirmationModal = (id?: string): void => {
    this.setState((prevState => ({
      showDeleteConfirmationModal: !prevState.showDeleteConfirmationModal,
      idToBeDeleted: id
    })));
  };

  setIdToBeDeleted = (idToBeDeleted: string): void => {
    this.setState({ idToBeDeleted });
  };

  setCurrentlyEditing = (type: string, obj: UnionOfEntities): void => {
    if (type === Views.payout) {
      const entity = obj as PaymentAccount;
      const payoutFinancialInstitutionId =
        entity.bankBranch?.financialInstitution.id || "";
      const paymentAccountType =
        (entity.accountType as string) || PaymentAccountTypesEnum.bankAccount;
      const accountNumber = entity.accountNumber || "";
      const institutions = this.getInstitutionsForPaymentType(paymentAccountType);

      this.setState(
        {
          payoutFinancialInstitutionId,
          payoutAccountBeingEdited: entity,
          paymentAccountType,
          accountNumber,
          institutions,
          accountValidationResponse: {
            status: true,
            message: "",
            name: entity.name || "",
            score: 100,
          },
        },
        () => {
          this.payoutReference.current?.setFieldsValue({
            accountType: entity.accountType,
            financialInstitutionId: payoutFinancialInstitutionId,
            accountNumber,
            bankBranchId: entity.bankBranch?.id,
            customerSubscriptionId: entity.customerSubscription?.id,
            bankAccountType: entity.bankAccountType,
            amount: entity.amount,
            authorizationFormUrl: entity.authorizationFormUrl,
            expirationDate: entity.expirationDate,
          });
        },
      );
    } else if (type === Views.debit) {
      const entity = obj as PaymentAccount;
      const debitFinancialInstitutionId =
        entity.bankBranch?.financialInstitution.id || "";
      const paymentAccountType =
        (entity.accountType as string) || PaymentAccountTypesEnum.bankAccount;
      const institutions = this.getInstitutionsForPaymentType(paymentAccountType);

      this.setState(
        {
          debitFinancialInstitutionId,
          debitAccountBeingEdited: entity,
          paymentAccountType,
          institutions,
        },
        () => {
          this.debitReference.current?.setFieldsValue({
            accountType: entity.accountType,
            financialInstitutionId: debitFinancialInstitutionId,
            bankBranchId: entity.bankBranch?.id,
            accountNumber: entity.accountNumber,
            bankAccountType: entity.bankAccountType,
            amount: entity.amount,
            name: entity.name,
            expirationDate: entity.expirationDate,
            authorizationFormUrl: entity.authorizationFormUrl,
            customerSubscriptionId: entity.customerSubscription?.id,
          });
        },
      );
    } else if (type === Views.customerAssociation) {
      const entity = obj as CustomerCompanyAssociation;
      this.setState({ customerAssociationBeingEdited: entity });
      this.associationReference.current?.setFieldsValue({
        companyId: entity.company?.id,
        relationship: entity.relationship,
        natureOfEmployment: entity.natureOfEmployment,
        frequencyOfIncome: entity.frequencyOfIncome,
        staffId: entity.staffId,
        influenceLevel: entity.influenceLevel,
        dateOfEmployment: entity.dateOfEmployment ? moment(entity.dateOfEmployment) : undefined,
      });
    } else {
      const beneficiary = obj as Beneficiary;
      const dobMoment = beneficiary.dateOfBirth
        ? moment(beneficiary.dateOfBirth)
        : undefined;
      this.setState(
        {
          beneficiaryBeingEdited: beneficiary,
          beneficiaryType:
            beneficiary.beneficiaryType || this.state.beneficiaryType,
        },
        () => {
          if (dobMoment) {
            this.handleDateOfBirthChange(dobMoment);
          }
          this.beneficiaryReference.current?.setFieldsValue({
            beneficiaryType: beneficiary.beneficiaryType,
            titleId: beneficiary.title?.id,
            firstName: beneficiary.firstName,
            middleName: beneficiary.middleName,
            lastName: beneficiary.lastName,
            emailAddress: beneficiary.emailAddress,
            idTypeId: beneficiary.idType?.id,
            idNumber: beneficiary.idNumber,
            dateOfBirth: dobMoment,
            benefitsPercentage: beneficiary.benefitsPercentage,
            beneficiaryAmount: beneficiary.beneficiaryAmount,
            customerRelationshipId: beneficiary.customerRelationship?.id,
            phoneNumber: beneficiary.phoneNumber,
            guardianName: beneficiary.guardianName,
            guardianEmail: beneficiary.guardianEmail,
            guardianPhoneNumber: beneficiary.guardianPhoneNumber,
            guardianAddress: beneficiary.guardianAddress,
            institutionName: beneficiary.institutionName,
            institutionAddress: beneficiary.institutionAddress,
            institutionPhoneNumber: beneficiary.institutionPhoneNumber,
            customerSubscriptionId: beneficiary.customerSubscription?.id,
          });
        },
      );
    }
  };

  resetState = (): void => {
    this.setState(
      {
        debitAccountBeingEdited: undefined,
        payoutAccountBeingEdited: undefined,
        beneficiaryBeingEdited: undefined,
        customerAssociationBeingEdited: undefined,
        payoutFinancialInstitutionId: "",
        debitFinancialInstitutionId: "",
        accountNumber: "",
        accountValidationResponse: undefined,
        paymentAccountType: PaymentAccountTypesEnum.bankAccount,
        institutions: this.getInstitutionsForPaymentType(PaymentAccountTypesEnum.bankAccount),
        beneficiaryType: BeneficiaryTypesEnum.individual,
        beneficiaryAge: 18,
        searchedCompanies: [],
      },
      () => {
        this.payoutReference.current?.resetFields();
        this.debitReference.current?.resetFields();
        this.beneficiaryReference.current?.resetFields();
        this.associationReference.current?.resetFields();
      },
    );
  };

  cancelBeneficiaryEdit = (): void => {
    this.setState(
      {
        beneficiaryBeingEdited: undefined,
        beneficiaryType: BeneficiaryTypesEnum.individual,
        beneficiaryAge: 18,
      },
      () => {
        this.beneficiaryReference.current?.resetFields();
      },
    );
    this.props.onCancelEdit?.();
  };

  cancelPaymentAccountEdit = (): void => {
    this.setState({
      payoutAccountBeingEdited: undefined,
      debitAccountBeingEdited: undefined,
    }, () => {
      this.payoutReference.current?.resetFields();
      this.debitReference.current?.resetFields();
    },
  );
    this.props.onCancelEdit?.();
  };

  cancelCustomerAssociationEdit = (): void => {
    this.setState({
      customerAssociationBeingEdited: undefined,
    }, () => {
      this.associationReference.current?.resetFields();
    },
  );
    this.props.onCancelEdit?.();
  };

  removePaymentInformation = (type: string, id: string): void => {
    const customer = this.props.opportunity.customer as Customer;

    if (type === Views.payout) {
      const updatedList = customer.customerPayoutAccounts.filter(
        (element: PaymentAccount): boolean => {
          return element.id !== id;
        },
      );
      this.props.updateCustomerPaymentInformation &&
        this.props.updateCustomerPaymentInformation({
          customerPayoutAccounts: updatedList,
        });
    } else {
      const updatedList = customer.customerDebitAccounts.filter(
        (element: PaymentAccount): boolean => {
          return element.id !== id;
        },
      );
      this.props.updateCustomerPaymentInformation &&
        this.props.updateCustomerPaymentInformation({
          customerDebitAccounts: updatedList,
        });
    }
  };

  handleCompanySearch = debounce((value: string): void => {
    if (!value) {
      this.setState({ searchedCompanies: [] });
      return;
    }

    this.setState({ isSearchingCompanies: true });

    request({
      query: SEARCH_COMPANY_SUMMARY,
      variables: { q: value, page: 1 },
    })
      .then((response: AxiosResponse<any>): void => {
        this.setState({
          searchedCompanies: response.data.data.companySearch.results,
        });
      })
      .catch((error): void => {
        message.error("Failed To Load Companies");
      })
      .finally((): void => {
        this.setState({ isSearchingCompanies: false });
      });
  }, DEBOUNCE_TIME);

  loadCustomerRelationships = (): void => {
    interface Response {
      data: {
        customerRelationships: SelectOptionsInterface[];
      };
    }
    request({
      query: GET_CUSTOMER_RELATIONSHIPS,
      variables: {},
    })
      .then((response: AxiosResponse<any>): void => {
        this.setState({
          relationships: response.data.data.customerRelationships,
        });
      })
      .catch((error): void => {
        message.error("Failed To Load Customer Relationship Types");
      });
  };

  loadIdTypes = (): void => {
    interface Response {
      data: {
        idTypes: Id[];
      };
    }

    request({
      query: GET_ID_TYPES,
      variables: {},
    })
      .then((response: AxiosResponse<any>): void => {
        this.setState({
          idTypes: response.data.data.idTypes,
        });
      })
      .catch((): void => {
        message.error("Failed to load ID Types");
      });
  };

  createDataBasedOnPayoutType = (
    data: Record<string, unknown>,
    financialInstitutionFor:
      | "payoutFinancialInstitutionId"
      | "debitFinancialInstitutionId",
  ): Record<string, unknown> => {
    if (this.state.paymentAccountType === PaymentAccountTypesEnum.momoAccount) {
      return {
        ...data,
        bankAccountType: BankAccountTypesEnum.currentAccount,
        bankBranchId:
          this.props.branchMap[this.state[financialInstitutionFor]][0]?.id,
      };
    }
    return data;
  };

  verifyAccount = async (
    type: "payout" | "debit" = "payout",
  ): Promise<void> => {
    interface Response {
      data: {
        validatePayoutAccountFuzzyMatch: ValidateAccount;
      };
    }

    const fieldsToBeValidated = [
      "financialInstitutionId",
      "accountNumber",
    ];

    const reference =
      type === "debit" ? this.debitReference : this.payoutReference;

    if (!reference.current) return;

    let data;

    try {
      data = await reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      await request({
        query: VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH,
        variables: {
          ...data,
          customerId: this.props.opportunity.customer?.id,
        },
      }).then(({ data }: AxiosResponse<Response>): void => {
        const response = data.data.validatePayoutAccountFuzzyMatch;
        const { status, score, name } = response;

        if (status === true) {
          if (score >= PAYOUT_VALIDATION_THRESHOLD) {
            this.setState({ accountValidationResponse: response });
            message.success("Account verified successfully");
            message.success(`Account name: ${name} (Match Score: ${Math.round(score)}%)`);
          } else {
            this.setState({ accountValidationResponse: undefined });
            message.error(`Customer name and Bank account name match too low: ${Math.round(score)}%. Must be above ${PAYOUT_VALIDATION_THRESHOLD}.`);
            message.success(`Bank account name: ${name}`);
          }
        } else {
          this.setState({ accountValidationResponse: undefined });
          message.error(response.message || "Error verifying account number");
        }
      });
    } catch (error) {
      message.error(`Error verifying account number - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createPayoutAccount = async (): Promise<void> => {
    const { accountNumber, accountValidationResponse } = this.state;

    interface Response {
      customerPayoutAccount?: PaymentAccount;
      errors: APIError[];
    }

    const fieldsToBeValidated = [
      "accountType",
      "financialInstitutionId",
      "customerSubscriptionId",
      "bankBranchId",
      "phoneNumber",
      "accountNumber",
      "accountType",
      "name",
    ];

    if (!this.payoutReference.current) return;

    let data;

    try {
      data = await this.payoutReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_PAYOUT_ACCOUNT,
        variables: {
          ...this.createDataBasedOnPayoutType(
            data,
            "payoutFinancialInstitutionId",
          ),
          customerId: this.props.opportunity.customer?.id,
          accountNumber,
          name: accountValidationResponse?.name,
          // accountValidationResponse is set only on a genuine pass at or above
          // PAYOUT_VALIDATION_THRESHOLD, so this is true only when the bank
          // actually confirmed the account belongs to the customer.
          isPayoutAccountVerified: !!accountValidationResponse,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCustomerPayoutAccount",
      );

      if (err) {
        this.payoutReference.current.setFields(err.map(mapAPIErrorToFormError));
        message.error(generateDisplayErrorMessage(err));
      } else if (
        mutation?.customerPayoutAccount?.customer?.customerPayoutAccounts
      ) {
        this.payoutReference.current.resetFields();
        this.setState({ accountNumber: "", accountValidationResponse: undefined, payoutFinancialInstitutionId: "", paymentAccountType: "" });
        this.props.updateCustomerPaymentInformation &&
          this.props.updateCustomerPaymentInformation({
            customerPayoutAccounts:
              mutation.customerPayoutAccount.customer.customerPayoutAccounts,
            customerDebitAccounts:
              mutation.customerPayoutAccount.customer.customerDebitAccounts,
          });
        message.success("Account created successfully");
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
    } catch (error) {
      message.error(`Failed to create a payout account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }

    this.resetState();
  };

  updatePayoutAccount = async (): Promise<void> => {
    const { accountNumber, accountValidationResponse } = this.state;

    interface Response {
      customerPayoutAccount?: PaymentAccount;
      errors: APIError[];
    }

    const fieldsToBeValidated = [
      "accountType",
      "customerSubscriptionId",
      "financialInstitutionId",
      "bankBranchId",
      "accountNumber",
      "accountType",
      "name",
    ];

    if (!this.payoutReference.current) return;

    let data;

    try {
      data = await this.payoutReference.current.validateFields(
        fieldsToBeValidated,
      );

    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_PAYOUT_ACCOUNT,
        variables: {
          ...this.createDataBasedOnPayoutType(
            data,
            "payoutFinancialInstitutionId",
          ),
          id: this.state.payoutAccountBeingEdited?.id,
          customerId: this.props.opportunity.customer?.id,
          accountNumber,
          name: accountValidationResponse?.name,
          // Editing without re-verifying clears the flag deliberately: the
          // account details may have changed, and a stale "verified" badge on
          // a changed account number is worse than asking for one more click.
          isPayoutAccountVerified: !!accountValidationResponse,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerPayoutAccount",
      );

      if (err) {
        this.payoutReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else if (
        mutation?.customerPayoutAccount?.customer?.customerPayoutAccounts
      ) {
        this.payoutReference.current.resetFields();
        message.success("Payout account updated successfully");
        this.props.updateCustomerPaymentInformation &&
          this.props.updateCustomerPaymentInformation({
            customerPayoutAccounts:
              mutation.customerPayoutAccount.customer.customerPayoutAccounts,
            customerDebitAccounts:
              mutation.customerPayoutAccount.customer.customerDebitAccounts,
          });
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
    } catch (error) {
      message.error(`Failed to create a update account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }

    this.resetState();
  };

  createDebitAccount = async (): Promise<void> => {
    const { authorizationFormUrl } = this.state;

    interface Response {
      customerDebitAccount?: PaymentAccount;
      errors: APIError[];
    }
    const fieldsToBeValidated = [
      "financialInstitutionId",
      "bankBranchId",
      "accountNumber",
      "accountType",
      "bankAccountType",
      "amount",
      "name",
      "expirationDate",
      "authorizationFormUrl",
      "customerSubscriptionId",
    ];

    if (!this.debitReference.current) return;

    let data;

    try {
      data = await this.debitReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_DEBIT_ACCOUNT,
        variables: {
          ...this.createDataBasedOnPayoutType(
            data,
            "debitFinancialInstitutionId",
          ),
          customerId: this.props.opportunity.customer?.id,
          authorizationFormUrl,
          name: this.state.accountValidationResponse?.name,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCustomerDebitAccount",
      );

      if (err) {
        this.debitReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else if (
        mutation?.customerDebitAccount?.customer?.customerDebitAccounts
      ) {
        this.debitReference.current.resetFields();
        this.props.updateCustomerPaymentInformation &&
          this.props.updateCustomerPaymentInformation({
            customerDebitAccounts:
              mutation.customerDebitAccount.customer.customerDebitAccounts,
            customerPayoutAccounts:
              mutation.customerDebitAccount.customer.customerPayoutAccounts,
          });
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
    } catch (error) {
      message.error(`Failed to create a debit account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
    this.resetState();
  };

  updateDebitAccount = async (): Promise<void> => {
    const { authorizationFormUrl } = this.state;

    interface Response {
      customerDebitAccount?: PaymentAccount;
      errors: APIError[];
    }
    const fieldsToBeValidated = [
      "expirationDate",
      "authorizationFormUrl",
      "financialInstitutionId",
      "bankBranchId",
      "accountNumber",
      "accountType",
      "bankAccountType",
      "amount",
      "name",
    ];

    if (!this.debitReference.current) return;

    let data;

    try {
      data = await this.debitReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_DEBIT_ACCOUNT,
        variables: {
          ...this.createDataBasedOnPayoutType(
            data,
            "debitFinancialInstitutionId",
          ),
          id: this.state.debitAccountBeingEdited?.id,
          customerId: this.props.opportunity.customer?.id,
          authorizationFormUrl,
          name: this.state.accountValidationResponse?.name,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerDebitAccount",
      );

      if (err) {
        this.debitReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else if (
        mutation?.customerDebitAccount?.customer?.customerDebitAccounts
      ) {
        this.debitReference.current.resetFields();
        this.props.updateCustomerPaymentInformation &&
          this.props.updateCustomerPaymentInformation({
            customerDebitAccounts:
              mutation.customerDebitAccount.customer.customerDebitAccounts,
            customerPayoutAccounts:
              mutation.customerDebitAccount.customer.customerPayoutAccounts,
          });
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
    } catch (error) {
      message.error(`Failed to update a debit account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
    this.resetState();
  };

  createBeneficiary = async (): Promise<void> => {
    interface Response {
      beneficiaries: Beneficiary[];
      errors: APIError[];
    }
    const fieldsToBeValidated: string[] = [
      "firstName",
      "lastName",
      "middleName",
      "dateOfBirth",
      "phoneNumber",
      "emailAddress",
      "customerRelationshipId",
      "customerSubscriptionId",
      "idTypeId",
      "idNumber",
      "benefitsPercentage",
      "guardianName",
      "guardianPhoneNumber",
      "guardianAddress",
      "guardianEmail",
      "institutionName",
      "institutionPhoneNumber",
      "institutionAddress",
      "beneficiaryType",
      "titleId",
    ];

    if (!this.beneficiaryReference.current) return;

    let data;

    try {
      data = await this.beneficiaryReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_BENEFICIARY,
        variables: {
          ...data,
        },
      });

      this.setState({
        isLoading: false,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCustomerBeneficiary",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.beneficiaryReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
      } else if (mutation.beneficiaries && this.props.updateBeneficiaryInformation) {
        const createdSubscriptionId =
          mutation.beneficiaries?.[0]?.customerSubscription?.id ||
          data.customerSubscriptionId;

        message.success("Beneficiary created successfully");
        this.props.updateBeneficiaryInformation({
          beneficiaries: mutation.beneficiaries,
          subscriptionId: createdSubscriptionId,
        });
        this.beneficiaryReference.current.resetFields();
        this.resetState();
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
        return;
      }
      this.beneficiaryReference.current.resetFields();
    } catch (error) {
      message.error(`Failed to create a update beneficiary - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  updateBeneficiary = async (): Promise<void> => {
    interface Response {
      beneficiaries: Beneficiary[];
      errors: APIError[];
    }
    const fieldsToBeValidated: string[] = [
      "firstName",
      "lastName",
      "middleName",
      "dateOfBirth",
      "phoneNumber",
      "emailAddress",
      "customerRelationshipId",
      "customerSubscriptionId",
      "idTypeId",
      "idNumber",
      "benefitsPercentage",
      "guardianName",
      "guardianPhoneNumber",
      "guardianAddress",
      "guardianEmail",
      "institutionName",
      "institutionPhoneNumber",
      "institutionAddress",
      "beneficiaryType",
      "titleId",
    ];

    if (!this.beneficiaryReference.current) return;

    let data;

    try {
      data = await this.beneficiaryReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_BENEFICIARY,
        variables: {
          ...data,
          id: this.state.beneficiaryBeingEdited?.id,
        },
      });

      this.setState({
        isLoading: false,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerBeneficiary",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.beneficiaryReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
      } else if (mutation.beneficiaries && this.props.updateBeneficiaryInformation) {
        const updatedSubscriptionId =
          mutation.beneficiaries?.[0]?.customerSubscription?.id ||
          data.customerSubscriptionId;

        message.success("Beneficiary updated successfully");
        this.beneficiaryReference.current.resetFields(fieldsToBeValidated);
        this.props.updateBeneficiaryInformation({
          beneficiaries: mutation.beneficiaries,
          subscriptionId: updatedSubscriptionId,
          previousSubscriptionId: this.state.beneficiaryBeingEdited?.customerSubscription?.id,
          beneficiaryId: this.state.beneficiaryBeingEdited?.id,
        });
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
    } catch (error) {
      message.error(`Failed to create a update beneficiary - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }

    this.resetState();
  };

  deletePayoutAccount = async (id: string): Promise<void> => {
    interface Response {
      result: boolean;
      errors: APIError[];
    }

    this.setState({
      isLoading: true,
      showDeleteConfirmationModal: false,
    });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_PAYOUT_ACCOUNT,
        variables: {
          id,
        },
      });

      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "deleteCustomerPayoutAccount",
      );

      if (mutation?.result) {
        this.removePaymentInformation(Views.payout, id);
        message.success("Payout account deleted successfully");
      } else {
        message.error("Failed to delete a payout account");
      }
    } catch (error) {
      message.error(`Failed to delete a payout account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  deleteDebitAccount = async (): Promise<void> => {
    const { idToBeDeleted } = this.state;

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    this.setState({
      isLoading: true,
      showDeleteConfirmationModal: false,
    });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_DEBIT_ACCOUNT,
        variables: {
          id: idToBeDeleted,
        },
      });

      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "deleteCustomerDebitAccount",
      );

      if (mutation?.result) {
        this.removePaymentInformation(Views.debit, idToBeDeleted as string);
      } else {
        message.error("Failed to delete a debit account");
      }
    } catch (error) {
      message.error(`Failed to delete a debit account - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  deleteBeneficiaryAccount = async (id: string): Promise<void> => {
    interface Response {
      result: boolean;
      beneficiaries: Beneficiary[];
      errors: APIError[];
    }

    this.setState({
      isLoading: true,
      showDeleteConfirmationModal: false,
    });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_BENEFICIARY,
        variables: {
          id,
        },
      });

      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "deleteCustomerBeneficiary",
      );

      if (mutation?.result && this.props.updateBeneficiaryInformation) {
        this.props.updateBeneficiaryInformation({
          beneficiaries: mutation.beneficiaries,
          subscriptionId: mutation.beneficiaries[0].customerSubscription?.id || "",
        });
        message.success("Beneficiary deleted successfully");
        this.props.loadOpportunity();
      } else {
        message.error("Failed to delete beneficiary");
      }
    } catch (error) {
      message.error(`Failed to delete beneficiary - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  deleteCustomerDocument = async (): Promise<void> => {
    this.props.customer?.customerInfoFileUploads &&
      (await this.deleteFile(this.state.idToBeDeleted ?? ""));
  };

  deleteSignedApplicationForm = async (): Promise<void> => {
    this.toggleDeleteConfirmationModal();
    this.props.opportunity.signedApplicationForm &&
      (await this.deleteFile(this.props.opportunity.signedApplicationForm.id));
  };

  deleteDirectDebitAuthorizationForm = async (): Promise<void> => {
    this.toggleDeleteConfirmationModal();
    this.props.opportunity.directDebitAuthorizationForm &&
      (await this.deleteFile(
        this.props.opportunity.directDebitAuthorizationForm.id,
      ));
  };

  generateTabMetaData = (item: string, length: number): JSX.Element => {
    const badgeStyle: CSSProperties = {
      backgroundColor: "#52c41a",
      marginLeft: "12px",
    };
    return (
      <span>
        {item} <Badge count={length} style={badgeStyle} />
      </span>
    );
  };

  deleteFile = async (id: string): Promise<void> => {
    interface Response {
      result: boolean;
      errors: APIError[];
    }

    this.setState({
      isLoading: true,
    });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_FILE,
        variables: { id: id },
      });

      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "deleteFileUpload",
      );

      if (mutation.result) {
        this.props.loadOpportunity();
      } else {
        message.error("Failed to delete file");
      }
    } catch (error) {
      message.error(`Failed to delete a file - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  setBeneficiaries = (): void => {
    const { subscriptions } = this.props;
    const customerBeneficiaries = subscriptions
      .map((subscription) =>
        subscription.customerBeneficiaries.map((beneficiary) => {
          return { ...beneficiary, subscriptionId: subscription.id };
        }),
      )
      .flat();
    this.setState({ customerBeneficiaries });
  };

  createAssociation = async (): Promise<void> => {
    const fieldsToBeValidated = ["companyId", "relationship", "staffId", "dateOfEmployment", "influenceLevel", "natureOfEmployment", "frequencyOfIncome"];

    if (!this.associationReference.current) return;

    let data: Record<string, unknown>;
    try {
      data = await this.associationReference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const existingActive = this.props.customerAssociations.find((a) => a.company?.id === data.companyId && a.isActive);

    if (existingActive) {
      // Active duplicate: prompt to confirm, and only send fields the user filled in so blanks don't wipe existing values.
      const filteredData = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== "" && v !== null && v !== undefined));
      Modal.confirm({
        title: "Existing association found",
        content: `This customer already has an active association with ${existingActive.company.name}. Override the current details with the values you entered?`,
        okText: "Override",
        cancelText: "Cancel",
        onOk: () => this.submitAssociationUpsert(filteredData),
      });
      return;
    }

    // No active duplicate. If an archived row exists for this company, the backend's find_or_create_by will revive it;
    // send the raw form data so the new values fully override anything stale on the archived row.
    this.submitAssociationUpsert(data);
  };

  submitAssociationUpsert = async (filteredData: Record<string, unknown>): Promise<void> => {
    interface Response {
      customerCompanyAssociation: CustomerCompanyAssociation;
      errors: APIError[];
    }

    if (!this.associationReference.current) return;

    const { customer } = this.props;

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_CUSTOMER_COMPANY_ASSOCIATION,
        variables: { ...filteredData, customerId: customer && customer.id },
      });

      this.setState({ isLoading: false });

      const { err, mutation } = responseHandler<Response>(response, "createCustomerCompanyAssociation");

      if (err) {
        this.associationReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else if (mutation && this.props.updateCustomerCompanyAssociationsInformation) {
        // Replace-by-id so the upsert path doesn't double-render an existing card.
        this.props.updateCustomerCompanyAssociationsInformation({
          associations: [...this.removeAssociation(mutation.customerCompanyAssociation.id), mutation.customerCompanyAssociation],
        });
        message.success("Association created successfully");
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
      this.associationReference.current.resetFields();
    } catch (error) {
      message.error(generateDisplayErrorMessage(error));
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
    this.resetState();
  };

  removeAssociation = (
    id: string | undefined,
  ): CustomerCompanyAssociation[] => {
    const associations = this.props.customerAssociations;
    const removeId = id ? id : "";
    const updatedAssociationList = associations.filter(
      (association: CustomerCompanyAssociation): boolean => {
        return association.id !== removeId;
      },
    );
    return updatedAssociationList;
  };

  updateAssociation = async (): Promise<void> => {
    interface Response {
      customerCompanyAssociation: CustomerCompanyAssociation;
      errors: APIError[];
    }

    const fieldsToBeValidated = [
      "companyId",
      "relationship",
      "staffId",
      "dateOfEmployment",
      "influenceLevel",
      "natureOfEmployment",
      "frequencyOfIncome"
    ];

    if (!this.associationReference.current) return;

    const { customer } = this.props;
    let data;

    try {
      data = await this.associationReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_COMPANY_ASSOCIATION,
        variables: {
          ...data,
          id: this.state.customerAssociationBeingEdited?.id,
          customerId: customer && customer.id,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerCompanyAssociation",
      );

      if (err) {
        this.associationReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
      } else if (
        mutation &&
        this.props.updateCustomerCompanyAssociationsInformation
      ) {
        this.props.updateCustomerCompanyAssociationsInformation({
          associations: [
            ...this.removeAssociation(
              this.state.customerAssociationBeingEdited?.id,
            ),
            mutation.customerCompanyAssociation,
          ],
        });
        message.success("Association updated successfully");
        this.props.loadOpportunity();
        this.props.onCancelEdit?.();
      }
      this.associationReference.current.resetFields();
    } catch (error) {
      message.error(`Failed to update an association - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
    this.resetState();
  };

  deleteAssociation = async (id: string): Promise<void> => {
    interface Response {
      customerCompanyAssociations: CustomerCompanyAssociation[];
      errors: APIError[];
    }

    this.setState({
      isLoading: true,
      showDeleteConfirmationModal: false,
    });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_CUSTOMER_ASSOCIATION,
        variables: {
          id,
        },
      });

      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "deleteCustomerCompanyAssociation",
      );

      if (
        mutation?.customerCompanyAssociations &&
        this.props.updateCustomerCompanyAssociationsInformation
      ) {
        this.props.updateCustomerCompanyAssociationsInformation({
          associations: mutation.customerCompanyAssociations,
        });
        this.props.loadOpportunity();
      } else {
        message.error("Failed to delete association");
      }
    } catch (error) {
      message.error(`Failed to delete association - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  setIsEmployee = async (id: string, isEmployee: boolean, successMessage: string, failureMessage: string): Promise<void> => {
    interface Response {
      customerCompanyAssociation: CustomerCompanyAssociation;
      errors: APIError[];
    }

    const association = this.props.customerAssociations.find(a => a.id === id);
    if (!association) {
      message.error(failureMessage);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_COMPANY_ASSOCIATION,
        variables: {
          id,
          customerId: this.props.customer?.id,
          companyId: association.company.id,
          isEmployee,
        },
      });

      const { mutation } = responseHandler<Response>(response, "updateCustomerCompanyAssociation");

      if (mutation?.customerCompanyAssociation) {
        // Local state update only — don't call loadOpportunity here, it would
        // refetch and pull the page out of edit mode.
        this.props.updateCustomerCompanyAssociationsInformation?.({
          associations: [...this.removeAssociation(id), mutation.customerCompanyAssociation],
        });
        message.success(successMessage);
      } else {
        message.error(failureMessage);
      }
    } catch (error) {
      message.error(failureMessage);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  markAsFormer = (id: string): Promise<void> =>
    this.setIsEmployee(id, false, "Marked as former employer", "Failed to mark as former");

  markAsCurrent = (id: string): Promise<void> =>
    this.setIsEmployee(id, true, "Marked as current employer", "Failed to mark as current");

  reactivateAssociation = async (id: string): Promise<void> => {
    interface Response {
      customerCompanyAssociations: CustomerCompanyAssociation[];
      errors: APIError[];
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: REACTIVATE_CUSTOMER_ASSOCIATION,
        variables: { id },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "reactivateCustomerCompanyAssociation",
      );

      if (mutation?.customerCompanyAssociations) {
        this.props.updateCustomerCompanyAssociationsInformation?.({
          associations: mutation.customerCompanyAssociations,
        });
        this.props.loadOpportunity();
        message.success("Association reactivated successfully");
      } else {
        message.error("Failed to reactivate association");
      }
    } catch (error) {
      message.error("Failed to reactivate association");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };


  async getTitles(): Promise<void> {
    interface Response {
      data: {
        titles: Title[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TITLES,
      });

      this.setState({
        titles: data.titles,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get titles  - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  }

  componentDidMount(): void {
    this.setBeneficiaries();
    this.loadCustomerRelationships();
    this.loadIdTypes();
    this.getTitles();
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.subscriptions !== this.props.subscriptions) {
      this.setBeneficiaries();
    }
    if (
      prevProps.financialInstitutions !== this.props.financialInstitutions &&
      this.state.institutions.length === 0 &&
      this.props.financialInstitutions.length > 0
    ) {
      this.setState({
        institutions: this.getInstitutionsForPaymentType(this.state.paymentAccountType),
      });
    }
  }

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url
    }));
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      showDeleteConfirmationModal,
      modalTarget,
      relationships,
      idTypes,
      titles,
      payoutAccountBeingEdited,
      debitAccountBeingEdited,
      beneficiaryBeingEdited,
      customerAssociationBeingEdited,
      paymentAccountType,
      accountValidationResponse,
      payoutFinancialInstitutionId,
      accountNumber,
      beneficiaryAge,
      beneficiaryType,
      customerBeneficiaries,
      institutions,
      searchedCompanies,
      isSearchingCompanies,
    } = this.state;
    const {
      isEditing,
      opportunity,
      showBeneficiarySection,
      showCustomerAssociationSection,
      showOnboardingDetailsSection,
      subscriptions,
      customerAssociations,
      activekey,
    } = this.props;
    const { customer } = opportunity;

    const modifiedSubscriptions = subscriptions.length
      ? filterActiveSubscriptions(subscriptions).map((subscription) => {
          return { id: subscription.id, name: subscription.product.name };
        })
      : opportunity.product
      ? [{ id: opportunity.product.id, name: opportunity.product.name }]
      : [];

    if (!customer) {
      return null;
    }

    const payoutBranch =
      this.props.branchMap[this.state.payoutFinancialInstitutionId];

    const debitBranch =
      this.props.branchMap[this.state.debitFinancialInstitutionId];

    const payoutFields: FormFieldConfig[] = createPaymentAccountFormFields({
      createPaymentAccount: this.createPayoutAccount,
      updatePaymentAccount: this.updatePayoutAccount,
      initialValue: payoutAccountBeingEdited,
      financialInstitutions: institutions,
      isEditing,
      name:
        payoutAccountBeingEdited?.name || generateNameFromCustomer(customer),
      handleAccountTypeChange: this.handlePaymentTypeChange,
      handleAccountTypeSelect: this.handlePaymentTypeSelect,
      handleFinancialInstitutionChange:
        this.handlePayoutFinancialInstitutionChange,
      paymentAccountType,
      branches: payoutBranch,
      subscriptions: modifiedSubscriptions,
      accountValidationResponse,
      payoutFinancialInstitutionId,
      accountNumber,
      verifyAccount: () => this.verifyAccount("payout"),
      institutions,
      handleAccountNumberChange: this.handleAccountNumberChange,
    });

    const debitFields: FormFieldConfig[] = createPaymentAccountFormFields({
      createPaymentAccount: this.createDebitAccount,
      updatePaymentAccount: this.updateDebitAccount,
      initialValue: debitAccountBeingEdited,
      isEditing,
      financialInstitutions: institutions,
      name: debitAccountBeingEdited?.name || generateNameFromCustomer(customer),
      handleAccountTypeChange: this.handlePaymentTypeChange,
      handleAccountTypeSelect: this.handlePaymentTypeSelect,
      handleFinancialInstitutionChange:
        this.handleDebitFinancialInstitutionChange,
      handleFileUploadChange: this.handleFileUploadChange,
      paymentAccountType,
      branches: debitBranch,
      subscriptions: modifiedSubscriptions,
      verifyAccount: () => this.verifyAccount("debit"),
      institutions,
      handleAccountNumberChange: this.handleAccountNumberChange,
      isDebit: true,
    });

    const beneficiaryFields: FormFieldConfig[] = createBeneficiaryFormFields({
      createBeneficiary: this.createBeneficiary,
      updateBeneficiary: this.updateBeneficiary,
      fileList: [],
      relationships,
      idTypes,
      titles,
      subscriptions: modifiedSubscriptions,
      initialValue: beneficiaryBeingEdited,
      isEditing,
      handleBeneficiaryTypeChange: this.handleBeneficiaryTypeChange,
      handleDateOfBirthChange: this.handleDateOfBirthChange,
      beneficiaryType,
      beneficiaryAge,
      cancelEdit: this.cancelBeneficiaryEdit,
    });

    const displayedCompanies = searchedCompanies.length > 0 ? searchedCompanies : (this.props.companies || []);
    const mergedCompanies = [...displayedCompanies];

    // Ensure the initial value is always in the dropdown list to show its name instead of its ID
    if (customerAssociationBeingEdited && customerAssociationBeingEdited.company) {
      const companyExists = mergedCompanies.find((c) => c.id === customerAssociationBeingEdited.company.id);
      if (!companyExists) {
        mergedCompanies.push(customerAssociationBeingEdited.company);
      }
    }

    const associationFields: FormFieldConfig[] =
      createCustomerCompanyAssociationFormFields({
        createAssociation: this.createAssociation,
        updateAssociation: this.updateAssociation,
        companies: mergedCompanies,
        isSearchingCompanies: isSearchingCompanies,
        handleCompanySearch: this.handleCompanySearch,
        initialValue: customerAssociationBeingEdited,
        isEditing,
      });

    const transformedUploadedDocuments = createUploadedDocumentObject({
      signedApplicationForm: opportunity.signedApplicationForm,
      customerInfoFileUploads: customer.customerInfoFileUploads,
      directDebitAuthorizationForm: opportunity.directDebitAuthorizationForm
    });

    const documentUploadsFields: FormFieldConfig[] =
      createDocumentUploadFormFields({
        opportunity,
        customer: this.props.customer,
        handleFileUploadSuccess: this.handleFileUploadSuccess,
        initialValues: {
          signedApplicationForm: opportunity.signedApplicationForm,
          directDebitAuthorizationForm:
            opportunity.directDebitAuthorizationForm,
        },
      });

    const entityListCreator = AssociatedEntityListCreator({
      setCurrentlyEditing: this.setCurrentlyEditing,
      toggleDeleteConfirmationModal: this.toggleDeleteConfirmationModal,
      setEntityToBeDeleted: this.setEntityToBeDeleted,
      setIdToBeDeleted: this.setIdToBeDeleted,
      isEditing,
      entitlement: entitlements.Customers.edit,
      deleteAssocation: this.deleteAssociation,
      deletePayoutAccount: this.deletePayoutAccount,
      deleteBeneficiaryAccount: this.deleteBeneficiaryAccount,
      markAsFormer: this.markAsFormer,
      markAsCurrent: this.markAsCurrent,
    });

    const arrayOfViews =
      this.props.visibleViews && this.props.visibleViews.length > 0
        ? this.props.visibleViews
        : (() => {
          const views = [Views.payout, Views.debit];
          if (showBeneficiarySection) {
            views.push(Views.beneficiary);
          }
          if (showOnboardingDetailsSection) {
            views.push(Views.documentUploads);
          }
          if (showCustomerAssociationSection) {
            views.push(Views.customerAssociation);
          }
          return views;
        })();

    let paymentAccountFieldsToHide: string[] = [];
    let beneficiaryDetailsFieldsToHide: string[] = [];

    if (paymentAccountType !== PaymentAccountTypesEnum.bankAccount) {
      paymentAccountFieldsToHide = [
        "bankAccountType",
        "bankBranchId",
      ];
    }

    if (
      beneficiaryType === BeneficiaryTypesEnum.individual &&
      beneficiaryAge < 18
    ) {
      beneficiaryDetailsFieldsToHide = [
        "institutionName",
        "institutionAddress",
        "institutionPhoneNumber",
      ];
    } else if (beneficiaryType === BeneficiaryTypesEnum.individual) {
      beneficiaryDetailsFieldsToHide = [
        "institutionName",
        "institutionAddress",
        "institutionPhoneNumber",
        "guardianName",
        "guardianPhoneNumber",
        "guardianAddress",
        "guardianEmail",
      ];
    } else {
      beneficiaryDetailsFieldsToHide = [
        "phoneNumber",
        "firstName",
        "middleName",
        "lastName",
        "guardianName",
        "guardianPhoneNumber",
        "guardianAddress",
        "guardianEmail",
        "emailAddress",
        "phone",
        "idTypeId",
        "idNumber",
        "dateOfBirth",
        "customerRelationshipId",
      ];
    }

    const showList = this.props.showList !== false;
    const renderInline = this.props.renderInline === true;
    const showInlineHeader = this.props.showInlineHeader === true;
    const suppressForm =
      this.props.hideFormOnDelete === true && showDeleteConfirmationModal;

    const buildView = (item: string): {
      form: JSX.Element;
      listCreator: JSX.Element[];
      arrayLength: number;
    } => {
      let form;
      let listCreator;
      let array: any[] = [];

              if (item === Views.payout) {
                form = (
                  <BaseForm
                    reference={this.payoutReference}
                    fields={payoutFields}
                    layout="vertical"
                    fieldsToHide={[
                      ...paymentAccountFieldsToHide,
                      "bankAccountType",
                      "authorizationFormUrl",
                      "expirationDate",
                      "amount",
                    ]}
                  />
                );
                listCreator = entityListCreator({
                  entity: Views.payout,
                  array: customer.customerPayoutAccounts,
                });
                array = customer.customerPayoutAccounts;
              } else if (item === Views.debit) {
                form = (
                  <BaseForm
                    reference={this.debitReference}
                    fields={debitFields}
                    layout="vertical"
                    fieldsToHide={paymentAccountFieldsToHide}
                  />
                );
                listCreator = entityListCreator({
                  entity: Views.debit,
                  array: customer.customerDebitAccounts,
                });
                array = customer.customerDebitAccounts;
              } else if (item === Views.beneficiary) {
                form = (
                  <BaseForm
                    key={`beneficiary-form-${beneficiaryBeingEdited?.id ?? "new"}`}
                    reference={this.beneficiaryReference}
                    fields={beneficiaryFields}
                    layout="vertical"
                    fieldsToHide={beneficiaryDetailsFieldsToHide}
                  />
                );
                listCreator = entityListCreator({
                  entity: Views.beneficiary,
                  array: customerBeneficiaries,
                });
                array = customerBeneficiaries;
              } else if (item === Views.customerAssociation) {
                form = (
                  <BaseForm
                    reference={this.associationReference}
                    fields={associationFields}
                    layout="vertical"
                    fieldsToHide={[]}
                  />
                );
                // Hide archived associations from both the badge count and the card list — Delete is one-way from the UI.
                const visibleAssociations = customerAssociations.filter((a) => a.isActive);
                listCreator = entityListCreator({
                  entity: Views.customerAssociation,
                  array: visibleAssociations,
                });
                array = visibleAssociations;
              } else {
                form = (
                  <BaseForm
                    reference={this.docsReference}
                    fields={documentUploadsFields}
                    layout="vertical"
                    fieldsToHide={[]}
                  />
                );
                listCreator = entityListCreator({
                  entity: Views.documentUploads,
                  array: transformedUploadedDocuments,
                });
                array = transformedUploadedDocuments;
              }

      return {
        form,
        listCreator,
        arrayLength: array.length,
      };
    };

    const renderView = (item: string, showHeader: boolean): JSX.Element => {
      if (suppressForm) {
        return <div key={item} style={{ minHeight: 0 }} />;
      }
      const { form, listCreator, arrayLength } = buildView(item);
      return (
        <div key={item}>
          {showHeader && (
            <div style={{ marginBottom: "12px" }}>
              {this.generateTabMetaData(item, arrayLength)}
            </div>
          )}
          <Spin spinning={isLoading}>
            <Row>
              <Col span={showList ? 13 : 24} style={colStyle}>
                {form}
              </Col>
              {showList && (
                <Col offset={1} span={8}>
                  {listCreator}
                </Col>
              )}
            </Row>
          </Spin>
        </div>
      );
    };

    return (
      <div style={{ width: "100%" }}>
        <Fragment>
          {ModalToggler({
            toggleModal: this.toggleDeleteConfirmationModal,
            showModal: showDeleteConfirmationModal,
            modalTarget: modalTarget,
            deletePayoutAccount: this.deletePayoutAccount,
            deleteDebitAccount: this.deleteDebitAccount,
            deleteBeneficiaryAccount: this.deleteBeneficiaryAccount,
            deleteSignedApplicationForm: this.deleteSignedApplicationForm,
            deleteAssociation: this.deleteAssociation,
            deleteCustomerDocument: this.deleteCustomerDocument
          })}
          {renderInline ? (
            suppressForm ? (
              <div />
            ) : (
              <div>
                {arrayOfViews.map((item: string) =>
                  renderView(item, showInlineHeader),
                )}
              </div>
            )
          ) : (
            <Collapse defaultActiveKey={activekey ? [activekey] : undefined}>
              {arrayOfViews.map((item: string): JSX.Element => {
                const { arrayLength } = buildView(item);
                return (
                  <Panel
                    header={this.generateTabMetaData(item, arrayLength)}
                    key={item}
                  >
                    {renderView(item, false)}
                  </Panel>
                );
              })}
            </Collapse>
          )}
        </Fragment>
      </div>
    );
  }
}
